import React, { useMemo } from 'react';
import { format, startOfDay, endOfDay, addMinutes } from 'date-fns';
import classnames from 'classnames';
import timezoneList from 'timezone-list';
import timezoneData from 'timezone-list/lib/data.json';
import turfDistance from '@turf/distance';
import { point } from '@turf/helpers';
import { Flex, Label, Text, Content, Toggle, Pane, Selector } from '@leverege/ui-elements';
import { ValuesCache } from '@leverege/value-cache';
import { GlobalState } from '@leverege/ui-redux';
import { LocationHoursModel, HoursModel } from '@leverege/recovr-models';
import S from './LocationHoursDetailRenderer.css';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const EMPTY = {};
function getEvenOddVariant(variantName, isEven, dVal) {
  const variantSuffix = isEven ? 'Even' : 'Odd';
  return `${variantName}${variantSuffix}|${variantName}${dVal ? `|${dVal}` : ''}`;
}
function getTimeList(minuteInterval = 15) {
  let start = startOfDay(new Date());
  const end = endOfDay(start);
  const timeList = [];
  while (start < end) {
    const formatted = format(start, 'hh:mma');
    timeList.push({
      name: formatted,
      value: formatted
    });
    start = addMinutes(start, minuteInterval);
  }
  return timeList;
}
function HoursRenderer(props) {
  const {
    model,
    label,
    onChange,
    eventData
  } = props;
  const nModel = model || HoursModel.create();
  const modelChange = evt => {
    const {
      data,
      value
    } = evt;
    const newModel = HoursModel[`set${data}`]?.(nModel, value) || nModel;
    if (newModel !== nModel) {
      onChange({
        data: eventData,
        value: newModel
      });
    }
  };
  const timeChange = evt => {
    const {
      data,
      value
    } = evt;
    if (value == null) {
      modelChange({
        data,
        value: HoursModel[`get${data}`]?.(nModel)
      });
    }
    modelChange({
      data,
      value
    });
  };
  const open = HoursModel.getOpen(nModel);
  const timeList = useMemo(() => {
    return getTimeList();
  }, []);
  return /*#__PURE__*/_jsxs(Pane, {
    variant: "locationHoursRow",
    className: classnames(S.rowGrid, open ? null : S.disabled),
    children: [/*#__PURE__*/_jsx(Text, {
      variant: "locationHoursLabel",
      children: label
    }), /*#__PURE__*/_jsx(Toggle, {
      variant: "locationHours",
      onChange: modelChange,
      eventData: "Open",
      value: open
    }), open ? /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Selector, {
        eventData: "OpenTime",
        className: S.picker,
        style: {
          overflow: 'visible'
        },
        onChange: timeChange,
        variant: getEvenOddVariant('operatingHoursDay', false, 'timeOnly'),
        toggleVariant: getEvenOddVariant('operatingHoursDay', false, 'timeOnly'),
        value: HoursModel.getOpenTime(nModel).toUpperCase(),
        values: timeList
      }), /*#__PURE__*/_jsx(Selector, {
        eventData: "CloseTime",
        className: S.picker,
        style: {
          overflow: 'visible'
        },
        onChange: timeChange,
        variant: getEvenOddVariant('operatingHoursDay', false, 'timeOnly'),
        toggleVariant: getEvenOddVariant('operatingHoursDay', false, 'timeOnly'),
        value: HoursModel.getCloseTime(nModel).toUpperCase(),
        values: timeList
      })]
    }) : /*#__PURE__*/_jsx(Text, {
      children: "Business Closed"
    })]
  });
}
export default class LocationHoursDetailRenderer {
  constructor(...rest) {
    this.tzCache = new ValuesCache();
  }
  getTimeZoneValue(item, cxt) {
    return this.tzCache.calc('timeZoneValue', (item, cxt) => {
      const {
        clientProps
      } = cxt;
      const {
        actions
      } = clientProps;
      const mdTimezone = item?.metadata?.locationHours?.timeZone;
      if (mdTimezone) {
        return mdTimezone;
      }
      const model = item?.metadata?.locationHours || LocationHoursModel.create();
      const nearestTz = this.getNearestTZ(item);
      const nModel = LocationHoursModel.setTimeZone(model, nearestTz);
      GlobalState.dispatch(actions.update({
        header: [{
          path: 'metadata/locationHours',
          value: nModel
        }]
      }));
      return null;
    }, item, cxt);
  }
  getNearestTZ(item) {
    const position = item?.data?.position;
    if (!position) {
      return 'America/New_York';
    }
    const here = point([position.lon, position.lat]);
    let shortestDistance = Number.MAX_SAFE_INTEGER;
    let nearestZone = 'America/New_York';
    const zoneNames = this.getTimezones(this.capitalize(item?.data?.country)) || this.getTimezones();
    zoneNames.forEach(zoneName => {
      const zones = timezoneData.zones;
      const zone = zones[zoneName] || zones[zoneName?.value];
      const zonePosition = point([zone.long, zone.lat]);
      const distance = turfDistance(here, zonePosition, {
        units: 'miles'
      });
      if (distance < shortestDistance) {
        shortestDistance = distance;
        nearestZone = zoneName.value || zoneName;
      }
    });
    return nearestZone;
  }
  getTimezones(country = 'United States') {
    // Add a space before all capital letters in the country name
    // eslint-disable-next-line no-param-reassign
    country = country.replace(/([a-z](?=[A-Z]))/g, '$1 ');
    const tzList = this.tzCache.calc('timeZoneList', country => {
      return timezoneList.getZoneByCountry(country).sort((a, b) => {
        return a > b ? 1 : -1;
      }).map(item => ({
        value: item,
        name: item
      }));
    }, country);
    return country === 'United States' ? tzList : tzList.concat([{
      value: 'America/New_York',
      name: 'America/New_York'
    }]);
  }
  capitalize(s) {
    if (typeof s !== 'string') return undefined;
    return String(s[0]).toUpperCase() + String(s).slice(1);
  }
  getValueComponent(object, cxt) {
    const {
      clientProps
    } = cxt;
    const {
      actions
    } = clientProps;
    const item = object?.data;
    const country = this.capitalize(item?.data?.country);
    const model = item?.metadata?.locationHours || LocationHoursModel.create();
    const timeZoneValue = this.getTimeZoneValue(item, cxt);
    const onChange = evt => {
      const {
        data,
        value
      } = evt;
      const nModel = LocationHoursModel[`set${data}`]?.(model, value) || model;
      if (nModel !== model) {
        GlobalState.dispatch(actions.update({
          header: [{
            path: 'metadata/locationHours',
            value: nModel
          }]
        }));
      }
    };
    return /*#__PURE__*/_jsxs(Content, {
      variant: "locationHours",
      className: S.content,
      relative: true,
      children: [/*#__PURE__*/_jsxs(Content.Header, {
        className: S.header,
        variant: "locationHoursHeader",
        children: [/*#__PURE__*/_jsx(Text, {
          variant: "locationHoursTitle|sectionBody",
          children: "Set the operating hours of your dealership below. These will be used to send you alerts after hours if a vehicle moves off lot."
        }), /*#__PURE__*/_jsxs(Flex, {
          className: S.borderTop,
          variant: "rowSpaceBetween",
          align: "center",
          children: [/*#__PURE__*/_jsx(Label, {
            className: S.tzLabel,
            children: "Select Timezone"
          }), /*#__PURE__*/_jsx(Selector, {
            className: S.tzSelector,
            variant: "timeZone",
            values: this.getTimezones(country).length > 0 ? this.getTimezones(country) : this.getTimezones(),
            value: timeZoneValue,
            eventData: "TimeZone",
            onChange: onChange
          })]
        })]
      }), /*#__PURE__*/_jsxs(Content.Area, {
        className: S.area,
        variant: "locationHoursContent",
        layout: "flex:col",
        children: [/*#__PURE__*/_jsxs(Pane, {
          className: S.rowGrid,
          children: [/*#__PURE__*/_jsx("div", {}), /*#__PURE__*/_jsx(Text, {
            variant: "caption",
            children: "Open"
          }), /*#__PURE__*/_jsx(Text, {
            variant: "caption",
            children: "Opening Time"
          }), /*#__PURE__*/_jsx(Text, {
            variant: "caption",
            children: "Closing Time"
          })]
        }), /*#__PURE__*/_jsx(HoursRenderer, {
          eventData: "MondayHours",
          label: "Monday",
          model: LocationHoursModel.getMondayHours(model),
          onChange: onChange
        }), /*#__PURE__*/_jsx(HoursRenderer, {
          eventData: "TuesdayHours",
          label: "Tuesday",
          model: LocationHoursModel.getTuesdayHours(model),
          onChange: onChange
        }), /*#__PURE__*/_jsx(HoursRenderer, {
          eventData: "WednesdayHours",
          label: "Wednesday",
          model: LocationHoursModel.getWednesdayHours(model),
          onChange: onChange
        }), /*#__PURE__*/_jsx(HoursRenderer, {
          eventData: "ThursdayHours",
          label: "Thursday",
          model: LocationHoursModel.getThursdayHours(model),
          onChange: onChange
        }), /*#__PURE__*/_jsx(HoursRenderer, {
          dropUp: true,
          eventData: "FridayHours",
          label: "Friday",
          model: LocationHoursModel.getFridayHours(model),
          onChange: onChange
        }), /*#__PURE__*/_jsx(HoursRenderer, {
          dropUp: true,
          eventData: "SaturdayHours",
          label: "Saturday",
          model: LocationHoursModel.getSaturdayHours(model),
          onChange: onChange
        }), /*#__PURE__*/_jsx(HoursRenderer, {
          dropUp: true,
          eventData: "SundayHours",
          label: "Sunday",
          model: LocationHoursModel.getSundayHours(model),
          onChange: onChange
        })]
      })]
    });
  }
  getComponents(object, cxt = EMPTY) {
    return [{
      style: {},
      component: this.getValueComponent(object, cxt)
    }];
  }
}